// The two things both surfaces of this app agree about.
//
// There are two now: the staff portal in App.js and the agent portal in
// AgentPortal.js. They are different audiences and share almost nothing —
// but they are the same firm's brand and they talk to the same service, and
// a second copy of either is how the agent's screen ends up a slightly
// different red, or pointed at a middleware that has moved.
//
// **The palette is neutral ink, not brown**, and the key names are historic:
// `brown` is `#333333` and `warmGrey` is white. They were renamed nowhere when
// the portal moved off the warm palette, because renaming them would have been
// a diff across every screen in App.js to no visible end — and the one thing
// that actually matters, that there is a single set of values, is what this
// file is for. The agent portal was drafted against the old values; taking it
// across without this would have left one screen brown and the rest ink.

export const T = {
  red: "#FE5442", brown: "#333333", warmGrey: "#FFFFFF", mauve: "#333333",
  white: "#FFFFFF", black: "#000000", bg: "#EFEFEF", bgAlt: "#E1E1E1",
  border: "#E3E3E3", borderMid: "#D2D2D2",
  text: "#333333", textSoft: "#666666", textFaint: "#8A8A8A",
  // **The firm's brand colour, and the main module's Fluent 2 neutrals.** The
  // brand is a CSS variable the firm's setting fills at start-up
  // (`applyBrand`), so a firm's colour reaches every screen without a
  // re-render; the value after the comma is Outlook blue, the default, and is
  // what a screen draws before the setting arrives. The shades are the
  // middleware's (`GET /practice/appearance`), never worked out here.
  brand: "var(--brand, #0F6CBD)", brandHover: "var(--brand-hover, #0E61AA)",
  brandPressed: "var(--brand-pressed, #083B68)", brandTint: "var(--brand-tint, #EBF3FC)",
  brandStroke: "var(--brand-stroke, #B4D6FA)", onBrand: "var(--on-brand, #FFFFFF)",
  card: "#FFFFFF", stroke: "#E0E0E0", strokeSubtle: "#F0F0F0", strokeInput: "#D1D1D1",
  fg1: "#242424", fg2: "#424242", fg3: "#616161", fg4: "#8A8A8A",
  danger: "#C50F1F", dangerTint: "#FDF3F4", dangerStroke: "#EEACB2",
  warn: "#BC4B09", warnTint: "#FFF9F5", warnStroke: "#FDCFB4",
  noteBg: "#FFFBE6", noteStroke: "#F2E383",
  shadowCard: "0 0 2px rgba(0,0,0,.08), 0 1px 2px rgba(0,0,0,.10)",
  // **The public new sale page's own palette** (`/sell`, `NewSale.js`), after
  // the Wispr Flow app and dashboard: a cool light-grey page, white cards,
  // charcoal ink, one violet accent (badges, what is done, what is active),
  // soft lavender for a secondary button, charcoal pills for the main one,
  // and cream only for the one banner card. Only that page reads these.
  sellBg: "#F3F3F5", sellCard: "#FFFFFF", sellInk: "#1F1E24", sellSoft: "#605F68", sellFaint: "#9A99A1", sellStar: "#F5B301",
  sellLine: "#E7E7EB", sellLineStrong: "#D6D6DC", sellWash: "#F7F7F9", sellTrack: "#ECECF0",
  sellAccent: "#5847D6", sellAccentTint: "#EEEBFE", sellLavender: "#DCD5FC", sellLavenderDeep: "#CCC2FA",
  sellGlow: "#8E80F2", sellCream: "#FDFDE6", sellCharcoal: "#2D2C33", sellOnDark: "#FFFFFF", sellSlate: "#7A8B97",
  sellShadow: "0 1px 2px rgba(20,20,30,.04), 0 8px 24px rgba(20,20,30,.05)",
  sellFrost: "rgba(255,255,255,.72)",
  // The draft's diagonal DRAFT — NOT FOR SIGNING marks.
  sellMark: "rgba(88,71,214,.13)",
  // Keys the brand palette needs, at /sell's own values.
  sellPartnerInk: "#1F1E24", sellRedSoft: "#DCD5FC", sellFieldTint: "#EEEBFE",
  sellPop: "#5847D6", sellPopTint: "#EEEBFE",
};

// The /sellv2 page in the brand refresh, monochrome: a white page in warm
// near-black ink and greys; Provey Red #FE5442 for buttons (with ink text —
// white on red fails AA) and, as `sellPop`, a mark on what is live, current
// or the client's own — never a surface; mist only for the big
// surfaces: the address banner, the start card and the desk behind the
// draft. sellAccent is ink here, so every "accent" (eyebrows, counts,
// links) reads as plain text. Same keys as T.sell*.
export const SELL_BRAND = {
  sellBg: "#FFFFFF", sellCard: "#FFFFFF", sellInk: "#2B2129", sellSoft: "#4C434D", sellFaint: "#978C93", sellStar: "#F5B301",
  sellLine: "rgba(43,33,41,.11)", sellLineStrong: "rgba(43,33,41,.20)", sellWash: "#F6F6F4", sellTrack: "#EFEFEC",
  sellAccent: "#5F5560", sellGlow: "#978C93", sellRedSoft: "#FFE1DA",
  // Red buttons with ink text: the one red on the page.
  sellCharcoal: "#FE5442", sellOnDark: "#2B2129", sellSlate: "#978C93",
  // Neutrals for every small detail: off-white, a step deeper when on.
  sellAccentTint: "#F6F6F4", sellLavender: "#ECECE8", sellLavenderDeep: "#DCDCD7", sellFieldTint: "#ECECE8",
  // Mist, for the big surfaces, and its ink.
  sellCream: "#E3EAEE", sellPartnerInk: "#3A4A55",
  sellShadow: "0 1px 2px rgba(43,33,41,.04), 0 8px 24px rgba(43,33,41,.05)",
  sellFrost: "rgba(255,255,255,.82)",
  sellMark: "rgba(43,33,41,.06)",
  // The brand red as a mark, not a surface: what is live, current or yours.
  sellPop: "#FE5442", sellPopTint: "rgba(254,84,66,.10)",
};

// The firm's shades onto the page, as the CSS variables `T.brand` and its
// neighbours read. Anything not a colour is ignored rather than written.
const BRAND_VARS = {
  brand: "--brand", brandHover: "--brand-hover", brandPressed: "--brand-pressed",
  brandTint: "--brand-tint", brandStroke: "--brand-stroke", onBrand: "--on-brand",
};
// Which palette a look wears. The practice look is Outlook blue until the firm
// saves a colour (`practiceTokens`); the classic look wears the firm's own
// starting colour — Provey's red. An older middleware sends only `tokens`.
export const brandTokensFor = (appearance, practice) =>
  (practice && appearance?.practiceTokens) || appearance?.tokens || null;

export const applyBrand = (tokens = {}, root = typeof document !== "undefined" ? document.documentElement : null) => {
  if (!root) return;
  for (const [key, cssVar] of Object.entries(BRAND_VARS)) {
    const value = tokens[key];
    if (typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value)) root.style.setProperty(cssVar, value);
  }
};

// Is this window the installed app (its own window, no browser bar)?
export const isInstalledApp = () => {
  try {
    return ["standalone", "window-controls-overlay", "minimal-ui"].some(m => window.matchMedia(`(display-mode: ${m})`).matches);
  } catch { return false; }
};

// **No zoom when a field is tapped, on iOS only.** Safari zooms in on a focused
// control under 16px and never zooms back out. `maximum-scale=1` stops that,
// and since iOS 10 Safari ignores it for pinch zoom, so the reader keeps theirs.
// Android honours it for pinch zoom too, and does not zoom on focus anyway, so
// it is left alone. An iPad asking for the desktop site says "Macintosh" and is
// told apart by its touch points. Inside the WordPress frame this does nothing:
// the top page's viewport decides there. See docs/decisions/theme-and-conventions.md.
export const isIOS = (ua = "", maxTouchPoints = 0) =>
  /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);

// **Where the duties form extension can run**: Chrome or Edge on a computer.
// A phone or tablet (Chrome on iOS included) runs no extensions, and Safari
// and Firefox can't load this one, so the portal says so rather than leaving
// a copied code to explain itself. See the middleware's docs/decisions/gov-forms.md.
export const dutiesBrowserOf = (ua = "", maxTouchPoints = 0) => {
  if (isIOS(ua, maxTouchPoints) || /Android|Mobile/.test(ua)) return "phone";
  if (/Firefox\//.test(ua)) return "other";
  if (/Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\//.test(ua)) return "other";
  return "ok";
};

export const noFocusZoomViewport = (content = "") =>
  /maximum-scale/.test(content) ? content : `${content}, maximum-scale=1`;

export const stopFocusZoom = (doc = typeof document !== "undefined" ? document : null,
  nav = typeof navigator !== "undefined" ? navigator : null) => {
  if (!doc || !nav || !isIOS(nav.userAgent, nav.maxTouchPoints)) return;
  const meta = doc.querySelector('meta[name="viewport"]');
  if (meta) meta.setAttribute("content", noFocusZoomViewport(meta.getAttribute("content") || ""));
};

// Not an env var, deliberately — same as it always was. When the Hosting
// rewrite lands (`/api/** → run`) this becomes "/api" and both surfaces move
// together, which is the point of it being here.
// **The one ladder from a field's type to the input that edits it.** Every
// surface that edits a field asks this rather than deciding for itself — the
// contract panel, the popover, the OC form in App.js, and the main module's
// section dialogs in Practice.js — so a control cannot look right on one screen
// and wrong on another. See docs/decisions/contract-preview-editing.md.
export const fieldInputProps = (field = {}) => (
  field.type === "date" ? { type: "date" }
    // A text box rather than a number box, so a `$` and commas can be typed —
    // a number input refuses both, which is how "$1,200" became impossible to
    // enter. `parseAmount` turns what is typed back into the number, and the
    // middleware prints it with its dollar sign. `decimal` keeps the numeric
    // keypad on a phone.
    : field.type === "money" ? { type: "text", inputMode: "decimal" }
      // An auction or settlement time — the practice record's own type.
      : field.type === "time" ? { type: "time" }
        : { type: "text" }
);

export const MIDDLEWARE_URL = "https://s32-middleware-1059323973409.australia-southeast2.run.app";

// The firm's own wordmark, served from `public/`. Both surfaces carry it — the
// staff header and the agent portal's top bar — and it is the same file the
// contract's cover page uses on the middleware, so a recipient sees one mark
// wherever they meet us. Red on transparent, so it sits on white unaltered.
export const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/provey-logo.png`;
// The black wordmark, for light pages that are not the staff portal (`/sell`).
export const LOGO_BLACK_SRC = `${process.env.PUBLIC_URL || ""}/provey-logo-black.png`;

// **Two shapes reach this, because two intakes write it.** Zap 1 sends
// Formidable's own `M/D/YYYY`; a direct Form 265 post sends the entry's
// creation date, which the middleware normalises to `YYYY-MM-DD` in Melbourne.
//
// The raw timestamp is read too — `2026-09-03 05:07:57` — and only its date
// part is shown. `openedDate` is set-once on the matter, deliberately, so the
// handful written before the middleware normalised it cannot be rewritten and
// would otherwise print a time to the second in the one column somebody reads
// at a glance. Reading it here fixes them without touching stored data.
//
// **It is here rather than in `App.js` because the agent portal prints the same
// column.** A second copy is how one screen comes to read `08/09/26` as 8
// September and the other as 9 August, on one matter, for the same firm.
export const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
export const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})\b/;

export const formatOpenedDate = (raw) => {
  if (!raw) return "—";
  const said = String(raw).trim();

  const iso = said.match(ISO_DATE);
  if (iso) {
    const [, year, month, day] = iso;
    return `${day}/${month}/${year}`;
  }

  const m = said.match(US_DATE);
  if (!m) return said;
  const [, month, day, year] = m;
  return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year.length === 2 ? `20${year}` : year}`;
};

// ── Offering something once ─────────────────────────────────────────────────
//
// Two things here introduce themselves and must not do it twice: the tour's
// nudge on a matter, and the reference panel on a first visit. Both are
// remembered per browser, and both are wrapped because a private window throws
// on access.
//
// **Unreadable storage reads as "not seen", and that reverses how the first of
// these shipped.** It answered `true` on a throw — the cautious-looking choice,
// and the wrong one: a browser that blocks site data would never have offered
// the tour to anybody, silently, which is the one failure nobody can report
// because there is nothing on screen to report. Offering it again is a card
// somebody closes; never offering it is a feature that does not exist.
//
// `remembered` is the fallback for exactly that browser: it holds the answer
// for as long as the page is open, so a blocked session offers the thing once
// rather than on every render.
//
// **One factory rather than a copy each**, because the reversal above is the
// load-bearing half and the second copy is the one that gets it backwards.
export const seenOnce = (key) => {
  let remembered = false;
  return {
    seen: () => {
      if (remembered) return true;
      try { return window.localStorage.getItem(key) === "1"; } catch { return false; }
    },
    remember: () => {
      remembered = true;
      try { window.localStorage.setItem(key, "1"); } catch { /* the flag above is the fallback */ }
    },
    // Only for a test that needs a fresh browser's answer — the flag above
    // outlives a `localStorage.clear()`, which is what a test reaches for.
    forget: () => { remembered = false; },
  };
};
